import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useSiteGroupCopyMutation } from "../../services/server/SiteGroupApi";
import type { SiteGroupCopyEntity } from "../../types/SiteGroupCopyEntity";
import siteGroupJson from "./site-group-copy.json";

export const SiteGroupCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = siteGroupJson as FormJson;

    const [formData, setFormData] = useState<SiteGroupCopyEntity>({} as SiteGroupCopyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useSiteGroupCopyMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: result.data.id,
                            resource: "Site Group",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.SITE_GROUPS}/${FORM_ACTION.EDIT}/${result.data.id}?id=${result.data.id}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    const { id } = useParams();
    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<SiteGroupCopyEntity>
                    name="SiteGroupCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
